import React, { useState, useCallback } from "react";
import { Box, Chip, makeStyles, OutlinedInput } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import CancelIcon from "@material-ui/icons/Cancel";
import { ChamberMemberCategory } from "../../../graphql/types";
import Loader from "../Loader/Loader";
import { useGetCategoryIdToHumanReadableName } from "../Hooks/useGetCategoryIdToHumanReadableName";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const useOutlinedInputStyles = makeStyles((theme) => ({
  root: {
    "& $notchedOutline": {
      borderColor: "#E1A731",
      borderWidth: "2px",
      borderRadius: "8px",
    },

    "&:hover $notchedOutline": {
      borderColor: "#E1A731",
    },

    "&$focused $notchedOutline": {
      borderColor: "#E1A731",
      borderWidth: "2px",
    },

    "&.Mui-error $notchedOutline": {
      borderColor: "#f44336",
      borderWidth: "2px",
    },
  },
  focused: {},
  notchedOutline: {},
}));

const useSelectStyles = makeStyles((theme) => ({
  root: {
    borderRadius: "8px !important",
  },
}));

export const useStyles = makeStyles({
  touchableRoot: {
    marginBottom: 20,
  },
  select: {
    borderRadius: "8px",
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent !important",
    },
    "&:before": {
      borderColor: "#24272C",
    },
    "&:after": {
      borderColor: "#24272C",
    },
    "&:not(.Mui-disabled):hover::before": {
      borderColor: "#24272C",
    },
    "& .MuiSelect-icon": {
      color: "#000000",
    },
  },
  root: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    flexWrap: "wrap",
    paddingRight: 2,
    paddingLeft: 2,
  },
  categoryButtonRoot: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: 115,
    width: 115,
    borderRadius: 20,
    backgroundColor: "white",
    elevation: 2,
    padding: 3,
  },
  iconRoot: {
    height: 50,
    width: 50,
  },
  iconImage: {
    flex: 1,
    width: "auto",
    height: "auto",
    resizeMode: "contain",
  },
  categoryText: { textAlign: "center" },
  formControl: {
    borderRadius: "8px",
    width: "100%",
  },
});

const Categories = ({
  selectedCategories,
  handleCategoriesChange,
  disableCategories,
  handleDelete,
  error,
}: {
  selectedCategories: any;
  handleCategoriesChange: any;
  disableCategories: boolean;
  handleDelete: any;
  error?: boolean;
}) => {
  const classes = useStyles();
  const outlinedInputClasses = useOutlinedInputStyles();
  const selectClasses = useSelectStyles();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const onChangeWithClose = useCallback((event: { target: { value: any } }) => {
    handleCategoriesChange(event);
    setOpen(false);
  }, [handleCategoriesChange]);

  const { categoryIdToHumanReadableName, allCategories } =
    useGetCategoryIdToHumanReadableName();

  if (!categoryIdToHumanReadableName || !allCategories) {
    return <Loader />;
  }

  return (
    <div>
      <FormControl className={classes.formControl}>
        <Select
          id="demo-multiple-chip"
          multiple
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          classes={selectClasses}
          className={classes.select}
          value={selectedCategories}
          displayEmpty
          // @ts-ignore
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 350,
                marginTop: 10,
                borderRadius: "8px",
                border: "1px solid #24272C",
              },
            },
            variant: "menu",
            getContentAnchorEl: null,
          }}
          onChange={onChangeWithClose}
          error={error}
          input={
            <OutlinedInput
              id="select-multiple-chip"
              classes={outlinedInputClasses}
            />
          }
          renderValue={(selected: any) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {selected.map((value: ChamberMemberCategory) => {
                return (
                  <Chip
                    key={value}
                    label={categoryIdToHumanReadableName[value]}
                    style={{
                      margin: "2px",
                      border: "1px solid #000000",
                      borderRadius: "5px",
                      backgroundColor: "#FFFFFF",
                      fontFamily: "DM Sans, sans-serif",
                      fontSize: "18px",
                      height: "29px",
                      color: "#000000",
                    }}
                    clickable
                    deleteIcon={
                      <CancelIcon
                        onMouseDown={(event: any) => event.stopPropagation()}
                        style={{
                          color: "#ED1C24",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    }
                    onDelete={() => handleDelete(value)}
                  />
                );
              })}
            </Box>
          )}
        >
          {(allCategories.slice() || [{ id: "a", name: "a" }])
            .sort(function (a, b) {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            })
            .map((category) => {
              const isSelected = selectedCategories.includes(category.id);
              return (
                <MenuItem
                  disabled={disableCategories}
                  key={category.id}
                  value={category.id}
                  style={{
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: "16px",
                    backgroundColor: isSelected ? "#E1A731" : "transparent",
                    color: isSelected ? "#FFFFFF" : "#000000",
                    borderBottom: "1px solid #F0F0F0",
                  }}
                >
                  {category.name}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    </div>
  );
};

export default Categories;
